<!--
  An explanation that is available without being in the way.

  The record header carries three controls whose reasoning runs to a paragraph each — what
  setting a reading direction does and does not decide, what boustrophedon means, why a sibling
  photograph inherits an answer. Printed inline, that is most of the rail spent on text an annotator reads once and
  then reads past a hundred times. Deleted, it is knowledge that only exists in a document
  nobody has open.

  So: a marker beside the label, and the paragraph on demand.

  **It is a button, not a `title=`.** A native tooltip cannot be reached from the keyboard at
  all, is not announced reliably, and vanishes on the touch devices this is occasionally used
  from. A button toggling a `role="note"` costs about forty lines and works everywhere. It also
  means the workspace's own keyboard map stands down while the marker has focus, because
  `isInteractiveTarget` already treats a focused button as the browser's business.
-->
<script lang="ts">
  import type { Snippet } from 'svelte';

  interface Props {
    /** What this explains, for the accessible name: "About reading direction". */
    label: string;
    children: Snippet;
  }

  const { label, children }: Props = $props();

  let open = $state(false);
  let root = $state<HTMLElement | null>(null);

  /**
   * Close when the pointer goes down anywhere else.
   *
   * Registered only while open, and on `pointerdown` rather than `click` so that the note is
   * already gone by the time a click lands on whatever is underneath it — otherwise dismissing
   * the note and pressing the control behind it are two separate gestures.
   */
  $effect(() => {
    if (!open) return;
    const onPointerDown = (event: Event) => {
      if (root && event.target instanceof Node && root.contains(event.target)) return;
      open = false;
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  });
</script>

<span class="infotip" bind:this={root}>
  <button
    type="button"
    class="marker"
    aria-expanded={open}
    aria-label={`About ${label}`}
    onclick={() => (open = !open)}
    onkeydown={(event) => {
      if (event.key === 'Escape' && open) {
        // Stopped here so the workspace's Esc — which deselects the box — does not also fire
        // on the way past. Dismissing a note should not cost the annotator their selection.
        event.stopPropagation();
        open = false;
      }
    }}
  >
    i
  </button>
  {#if open}
    <span class="note small" role="note">
      {@render children()}
    </span>
  {/if}
</span>

<style>
  .infotip {
    position: relative;
    display: inline-flex;
  }

  .marker {
    width: 14px;
    height: 14px;
    padding: 0;
    border: 1px solid var(--line-strong);
    border-radius: 50%;
    background: transparent;
    color: var(--muted);
    font-size: 0.66rem;
    font-style: italic;
    line-height: 1;
    cursor: pointer;
  }

  .marker:hover,
  .marker[aria-expanded='true'] {
    border-color: var(--accent);
    color: var(--accent);
  }

  /*
    Left-anchored and capped, because the rail is about 420px and these markers sit beside
    labels at its left edge. `max-width` in `min()` with a viewport unit so the note still fits
    when the rail is collapsed on a narrow window.
  */
  .note {
    position: absolute;
    top: 20px;
    left: -6px;
    z-index: 20;
    width: max-content;
    max-width: min(300px, 78vw);
    background: rgba(18, 21, 26, 0.98);
    border: 1px solid var(--line-strong);
    border-radius: 5px;
    padding: 7px 9px;
    color: var(--text);
    font-style: normal;
    text-align: left;
    white-space: normal;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
  }
</style>
